import { AddressType, DealerAddressType } from 'components/common/CommonTypes';
import { CommonValidation } from 'components/common/commonValidation/commonValidation';
import DealerAddress from 'components/common/dealerAddress/DealerAddress';
import DealerAddressLookup from 'components/common/dealerAddress/DealerAddressLookup';
import ErrorSummary from 'components/common/ErrorSummary/ErrorSummary';
import FormInput from 'components/common/FormInput';
import NumberInput from 'components/common/numberInput/NumberInput';
import { TyreRepairRequestContext } from 'context/TyreRepairRequestContext';
import i18next from 'i18next';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import AddressManager from 'components/common/address/AddressManager';
import { GoogleAnalytics } from 'classes/GoogleAnalytics';
import { Locations, PageNames } from 'Constants/Common';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'state/hooks';

type TyreGeneralDetailsProps = {
    cancelConfirmText: string;
};

const TyreGeneralDetails = ({
    cancelConfirmText
}: TyreGeneralDetailsProps): JSX.Element => {
    const modalAddress = useRef(null);
    const [defaultDealerAddresses, setDefaultDealerAddresses] = useState<
        AddressType[]
    >([]);
    const { t, i18n } = useTranslation();
    const isSiteLocationUs = i18n.language?.toLowerCase() === Locations.Us;
    const showRepairAddress = useAppSelector((s) => s.site.showRepairAddress);
    const displayRepairAddress =
        showRepairAddress &&
        (defaultDealerAddresses.length > 0 || !isSiteLocationUs);

    const {
        tyreRepairRequest,
        onChangeMileage,
        onDealerAddressUpdate,
        onCancelClick,
        onContinueRepairRequest,
        onCancelRepairRequest,
        onCancelUndo,
        onRepairAddressSelectionChanged,
        onDefaultDealerAddressUpdate
    } = useContext(TyreRepairRequestContext);

    const {
        scrollToAddress,
        selectedTyres,
        showCancelAlert,
        repairAddressSelectedIndex,
        repairAddresses,
        dealerAddress
    } = tyreRepairRequest;

    const errorlist = (): Array<string | undefined> => {
        const values = [
            tyreRepairRequest.tyreFormErrors?.mileage,
            displayRepairAddress
                ? tyreRepairRequest.tyreFormErrors?.noRepairAddressSelected
                : undefined
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    const onChangeTyreRequestMileage = (
        e: React.FormEvent<HTMLInputElement>
    ): void => {
        const value = (e.target as HTMLInputElement).value;
        if (CommonValidation.isEmpty(value)) {
            onChangeMileage('');
        } else if (CommonValidation.isInteger(value)) {
            onChangeMileage(value);
        }
    };

    const onDealerAddressUpdated = (dealerAddress: DealerAddressType): void => {
        onDealerAddressUpdate(dealerAddress);
    };
    const onDefaultAddressSelectionChanged = (
        selectedAddressIndexNumber: number
    ) => {
        onRepairAddressSelectionChanged(selectedAddressIndexNumber);
    };

    const cancelButtonText =
        selectedTyres.length > 1
            ? t('cancel_repair_requests')
            : t('cancel_repair_request');

    useEffect(() => {
        if (scrollToAddress && modalAddress.current) {
            modalAddress.current.focus();
        }
        GoogleAnalytics.setPageView(PageNames.AlloyGeneral);
    }, []);

    useEffect(() => {
        if (
            showRepairAddress &&
            isSiteLocationUs &&
            tyreRepairRequest.dealerAddress?.dealerStreetAddress
        ) {
            const repairAddress: AddressType = {
                addressLine1: dealerAddress.dealerStreetAddress.street || '',
                townOrCity: dealerAddress.dealerStreetAddress.city || '',
                postcode:
                    dealerAddress.dealerStreetAddress.zipOrPostalCode || '',
                country: dealerAddress.locationCountry || ''
            };

            onDefaultDealerAddressUpdate(repairAddress);
            setDefaultDealerAddresses([repairAddress]);
            onDefaultAddressSelectionChanged(repairAddressSelectedIndex + 1);
        }
    }, [
        tyreRepairRequest.dealerAddress?.dealerStreetAddress?.street,
        showRepairAddress
    ]);

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='mileage-forminput'
                        label={t('alloy_repair_request.section_2_title')}
                        errorText={tyreRepairRequest.tyreFormErrors?.mileage}>
                        <div className='row repair-request-field-container'>
                            <div className='col-xs-12 col-lg-6'>
                                <NumberInput
                                    name='approx-mileage'
                                    value={
                                        tyreRepairRequest?.mileage.toString() ||
                                        ''
                                    }
                                    onChange={onChangeTyreRequestMileage}
                                    decimalPlaces={0}
                                />
                            </div>
                        </div>
                    </FormInput>
                    {isSiteLocationUs && (
                        <FormInput
                            id='find-dealer-forminput'
                            label={t('find_dealer')}>
                            <DealerAddressLookup
                                dealerAddress={tyreRepairRequest?.dealerAddress}
                                onDealerAddressUpdated={onDealerAddressUpdated}
                            />
                            {tyreRepairRequest?.dealerAddress
                                ?.dealerStreetAddress && (
                                <DealerAddress
                                    address={tyreRepairRequest?.dealerAddress}
                                />
                            )}
                        </FormInput>
                    )}
                    {displayRepairAddress && (
                        <FormInput
                            id='repair-address-forminput'
                            label={t('alloy_repair_request.section_3_title')}
                            errorText={
                                tyreRepairRequest.tyreFormErrors
                                    ?.noRepairAddressSelected
                            }>
                            <div className='ml-3'>
                                <p>
                                    {t('alloy_repair_request.section_3_para_1')}
                                </p>
                                {i18next.language.toLocaleLowerCase() !==
                                    'us' && (
                                    <p>
                                        {t(
                                            'alloy_repair_request.section_3_para_2'
                                        )}
                                    </p>
                                )}
                            </div>
                            <div>
                                {isSiteLocationUs &&
                                    tyreRepairRequest?.dealerAddress
                                        ?.dealerStreetAddress && (
                                        <AddressManager
                                            addressSelectedIndex={
                                                repairAddressSelectedIndex
                                            }
                                            addresses={defaultDealerAddresses}
                                            onAddressSelectionChanged={
                                                onDefaultAddressSelectionChanged
                                            }
                                        />
                                    )}
                                {!isSiteLocationUs && (
                                    <AddressManager
                                        addressSelectedIndex={
                                            repairAddressSelectedIndex
                                        }
                                        addresses={repairAddresses}
                                        onAddressSelectionChanged={
                                            onRepairAddressSelectionChanged
                                        }
                                    />
                                )}
                            </div>
                            <div tabIndex={0} ref={modalAddress}></div>
                        </FormInput>
                    )}
                </div>
                <div className='card-footer page-content-footer'>
                    {tyreRepairRequest.tyreFormErrors && (
                        <ErrorSummary
                            title={t('tyre_general_errors')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    id='btnContinueGeneral'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onContinueRepairRequest}>
                    {t('button_label.continue')}
                </button>
                <button
                    id='btnCancelGeneral'
                    data-testid='danger-button'
                    className='btn btn-danger progress-button'
                    onClick={onCancelClick}>
                    {cancelButtonText}
                </button>
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};

export default TyreGeneralDetails;
